import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { Immunization } from '@medplum/fhirtypes';
import {
  CVX_PLACEHOLDER_CODES,
  CVX_SYSTEM,
  getCvxEntry,
  type ImmunizationGroupCode,
  VACCINE_GROUP_NAMES,
  VACCINE_GROUPS,
} from './cvx';
import { IDENTIFIER_SYSTEMS } from './identifiers';

export type ImmunizationRecord = WithId<Immunization>;

/**
 * What a group holds. `vaccine` groups are the schedule; `test` (tuberculin skin tests) and
 * `immune-globulin` are immunity records that are not doses; `history` is evidence of immunity by
 * prior disease; `unresolved` rows carry a name no table recognises and stand alone under it.
 */
export type ImmunizationGroupKind = 'vaccine' | 'test' | 'immune-globulin' | 'history' | 'unresolved';

/** One administration: the record shown for it plus any same-day rows for the same group that
 * describe the same shot (an exporter's specific-product row next to its "unspecified" twin). */
export type ImmunizationDose = {
  record: ImmunizationRecord;
  merged: ImmunizationRecord[];
  /** `yyyy-MM-dd` day of administration, or `undefined` when the record is undated. */
  date: string | undefined;
  /** Canonical one-line name for the displayed record, or its source text when uncoded. */
  shortName: string | undefined;
};

export type ImmunizationGroup = {
  /** Stable key: the CDC group code, or a normalised source label for rows no table resolves. */
  key: string;
  kind: ImmunizationGroupKind;
  /** The CDC group code, when the rows resolved to one. */
  group: ImmunizationGroupCode | undefined;
  /** Source text naming the group when there is no code to translate (history and unresolved rows). */
  label: string | undefined;
  /** Newest first; undated doses last. */
  doses: ImmunizationDose[];
  mostRecent: string | undefined;
};

export type GroupedImmunizations = {
  /** Vaccine groups in schedule order, then unresolved rows, then tests, immune globulins and
   * disease history. */
  groups: ImmunizationGroup[];
  /** Placeholder rows an exporter filed as immunizations ("No data available for this section"),
   * and rows with no name at all. */
  hidden: ImmunizationRecord[];
};

type TextGroup = ImmunizationGroupCode | 'HISTORY' | 'PLACEHOLDER';

/**
 * Word-bounded phrases that place an uncoded row in a group. Non-vaccine entries are tried first so
 * "Varicella, Hx of Disease" is disease history rather than a varicella dose and "rabies immune
 * globulin" is not a rabies vaccine. A phrase may sit under several groups: a combination product's
 * text names every component.
 */
export const IMMUNIZATION_TEXT_ALIASES: Record<TextGroup, string[]> = {
  PLACEHOLDER: ['no data available', 'no immunization', 'no vaccine administered', 'none recorded'],
  HISTORY: ['hx of disease', 'history of disease', 'hx of', 'immunity'],
  TST: ['ppd', 'tuberculin', 'tb skin test', 'tb test', 'mantoux', 'tine test'],
  IG: ['immune globulin', 'immunoglobulin', 'antitoxin', 'hbig', 'tig', 'rig', 'vzig', 'igiv', 'ivig'],
  FLU: ['influenza', 'flu', 'fluarix', 'fluzone', 'flucelvax', 'afluria', 'flublok', 'flumist', 'fluad', 'flulaval'],
  'COVID-19': ['covid', 'sars-cov-2', 'coronavirus', 'comirnaty', 'spikevax', 'novavax', 'nuvaxovid'],
  TDAP: ['tdap', 'boostrix', 'adacel'],
  Td: ['td', 'tenivac', 'tdvax', 'tetanus and diphtheria', 'tetanus-diphtheria', 'tetanus toxoid'],
  PneumoPCV: ['prevnar', 'pcv', 'vaxneuvance', 'capvaxive', 'pneumococcal conjugate', 'pneumococcal'],
  PneumoPPV: ['pneumovax', 'ppsv', 'ppv23', 'pneumococcal polysaccharide'],
  ZOSTER: ['zoster', 'shingrix', 'shingles', 'zostavax'],
  RSV: ['rsv', 'respiratory syncytial', 'arexvy', 'abrysvo', 'beyfortus', 'nirsevimab', 'synagis'],
  HepB: ['hepatitis b', 'hep b', 'hepb', 'engerix', 'recombivax', 'heplisav', 'twinrix', 'pediarix', 'vaxelis'],
  HepA: ['hepatitis a', 'hep a', 'hepa', 'havrix', 'vaqta', 'twinrix'],
  MMR: ['mmr', 'measles', 'mumps', 'rubella', 'priorix', 'proquad'],
  VARICELLA: ['varicella', 'varivax', 'chickenpox', 'proquad'],
  HPV: ['hpv', 'papilloma', 'gardasil', 'cervarix'],
  MENING: ['menactra', 'menveo', 'menquadfi', 'menacwy', 'mcv4', 'mpsv4', 'meningococcal'],
  MeningB: ['bexsero', 'trumenba', 'menb', 'meningococcal b', 'meningococcal group b'],
  DTAP: ['dtap', 'dtp', 'daptacel', 'infanrix', 'pediarix', 'pentacel', 'kinrix', 'quadracel', 'vaxelis'],
  HIB: ['hib', 'haemophilus', 'acthib', 'pedvaxhib', 'hiberix', 'pentacel', 'vaxelis'],
  POLIO: ['polio', 'ipv', 'opv', 'ipol', 'pediarix', 'pentacel', 'kinrix', 'quadracel', 'vaxelis'],
  ROTAVIRUS: ['rotavirus', 'rotateq', 'rotarix'],
  ADENO: ['adenovirus'],
  ANTHRAX: ['anthrax', 'biothrax'],
  BCG: ['bcg', 'bacillus calmette'],
  Chikungunya: ['chikungunya', 'ixchiq'],
  cholera: ['cholera', 'vaxchora'],
  DENGUE: ['dengue', 'dengvaxia'],
  Ebola: ['ebola', 'ervebo'],
  'FLU-Avian-H5': ['avian influenza', 'h5n8'],
  'H1N1 flu': ['h1n1'],
  'H5N1 flu': ['h5n1'],
  'Japanese encephalitis': ['japanese encephalitis', 'ixiaro'],
  'Lyme disease': ['lyme'],
  Orthopoxvirus: ['smallpox', 'monkeypox', 'mpox', 'vaccinia', 'jynneos', 'acam2000'],
  Plague: ['plague'],
  RABIES: ['rabies', 'imovax', 'rabavert'],
  'Tick-borne encephalitis': ['tick-borne', 'tick borne', 'ticovac'],
  TYPHOID: ['typhoid', 'typhim', 'vivotif'],
  VEE: ['venezuelan equine'],
  YELLOWFEVER: ['yellow fever', 'yf-vax', 'stamaril'],
};

const NON_VACCINE_TEXT_GROUPS: readonly TextGroup[] = ['PLACEHOLDER', 'HISTORY', 'TST', 'IG'];

const normalize = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const aliasPattern = (alias: string): RegExp => new RegExp(`(^|[^a-z0-9])${escapeRegExp(alias)}($|[^a-z0-9])`);

const textMatchesGroup = (text: string, group: TextGroup): boolean =>
  IMMUNIZATION_TEXT_ALIASES[group].some((alias) => aliasPattern(alias).test(text));

/** Every name a row carries, joined, so aliases can match the source text or any coding display. */
const recordNameText = (record: Immunization): string => {
  const names = [record.vaccineCode?.text, ...(record.vaccineCode?.coding ?? []).map((c) => c.display)];
  return normalize(names.filter((name): name is string => Boolean(name)).join(' | '));
};

const cvxCode = (record: Immunization): string | undefined =>
  record.vaccineCode?.coding?.find((c) => c.system === CVX_SYSTEM)?.code;

const sourceName = (record: Immunization): string | undefined => formatCodeableConcept(record.vaccineCode) || undefined;

type Resolution =
  | { kind: 'grouped'; groups: readonly ImmunizationGroupCode[]; shortName: string; unspecified: boolean }
  | { kind: 'history' | 'unresolved'; label: string }
  | { kind: 'placeholder' };

const kindForGroup = (group: ImmunizationGroupCode): ImmunizationGroupKind => {
  if (group === 'TST') return 'test';
  if (group === 'IG') return 'immune-globulin';
  return 'vaccine';
};

const resolveByText = (record: Immunization): Resolution => {
  const text = recordNameText(record);
  const label = sourceName(record);
  if (!text || !label) return { kind: 'placeholder' };

  for (const group of NON_VACCINE_TEXT_GROUPS) {
    if (!textMatchesGroup(text, group)) continue;
    if (group === 'PLACEHOLDER') return { kind: 'placeholder' };
    if (group === 'HISTORY') return { kind: 'history', label };
    return { kind: 'grouped', groups: [group], shortName: label, unspecified: false };
  }

  const groups = VACCINE_GROUPS.filter((group) => textMatchesGroup(text, group));
  if (groups.length === 0) return { kind: 'unresolved', label };
  return { kind: 'grouped', groups, shortName: label, unspecified: false };
};

const resolve = (record: Immunization): Resolution => {
  const code = cvxCode(record);
  if (code !== undefined && CVX_PLACEHOLDER_CODES.has(code)) return { kind: 'placeholder' };
  const entry = getCvxEntry(code);
  if (!entry) return resolveByText(record);
  return { kind: 'grouped', groups: entry.groups, shortName: entry.shortName, unspecified: entry.unspecified };
};

/** Whether the row was ingested from an outside source rather than authored in Liza. */
export const isImportedImmunization = (record: Immunization): boolean =>
  (record.identifier ?? []).some((id) => id.system === IDENTIFIER_SYSTEMS.EXTERNAL_ORIGINAL_ID);

/** The administration day as the first ten characters of `occurrenceDateTime`. Imported values mix
 * date-only strings with offset datetimes, and parsing either shifts the day west of UTC, so the
 * key is taken from the text as written. */
export const immunizationDay = (record: Immunization): string | undefined =>
  record.occurrenceDateTime ? record.occurrenceDateTime.slice(0, 10) : undefined;

const parseInstant = (value: string | undefined): number => {
  if (!value) return 0;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? 0 : ms;
};

type Candidate = { record: ImmunizationRecord; unspecified: boolean; coded: boolean };

/** Higher is more authoritative: a specific code beats an uncoded name, which beats an "unspecified"
 * code. */
const specificity = (candidate: Candidate): number => {
  if (candidate.unspecified) return 0;
  return candidate.coded ? 2 : 1;
};

/** Most authoritative first: specificity, then a Liza-authored row over an import of the same shot,
 * then a lot number present, then newest `recorded` and `lastUpdated`. Medplum ids are random, so
 * `id` is only the deterministic last resort. */
const byAuthority = (a: Candidate, b: Candidate): number => {
  const bySpecificity = specificity(b) - specificity(a);
  if (bySpecificity !== 0) return bySpecificity;
  const byOrigin = Number(isImportedImmunization(a.record)) - Number(isImportedImmunization(b.record));
  if (byOrigin !== 0) return byOrigin;
  const byLot = Number(Boolean(b.record.lotNumber)) - Number(Boolean(a.record.lotNumber));
  if (byLot !== 0) return byLot;
  const byRecorded = parseInstant(b.record.recorded) - parseInstant(a.record.recorded);
  if (byRecorded !== 0) return byRecorded;
  const byUpdated = parseInstant(b.record.meta?.lastUpdated) - parseInstant(a.record.meta?.lastUpdated);
  if (byUpdated !== 0) return byUpdated;
  return a.record.id.localeCompare(b.record.id);
};

const byNewestDoseThenId = (a: ImmunizationDose, b: ImmunizationDose): number => {
  if (a.date !== b.date) {
    if (a.date === undefined) return 1;
    if (b.date === undefined) return -1;
    return b.date.localeCompare(a.date);
  }
  return a.record.id.localeCompare(b.record.id);
};

type PendingDay = { candidates: Candidate[]; date: string | undefined };

type PendingGroup = {
  key: string;
  kind: ImmunizationGroupKind;
  group: ImmunizationGroupCode | undefined;
  label: string | undefined;
  /** Same-day candidates keyed by day; undated rows are keyed by id so they never merge. */
  days: Map<string, PendingDay>;
};

const KIND_ORDER: Record<ImmunizationGroupKind, number> = {
  vaccine: 0,
  unresolved: 1,
  test: 2,
  'immune-globulin': 3,
  history: 4,
};

/** Display position of each vaccine group: `VACCINE_GROUPS` is declared in schedule order. */
const GROUP_POSITION = new Map<string, number>(VACCINE_GROUPS.map((group, index) => [group, index]));

const byScheduleThenName = (a: ImmunizationGroup, b: ImmunizationGroup): number => {
  const byKind = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  if (byKind !== 0) return byKind;
  const byPosition = (GROUP_POSITION.get(a.key) ?? -1) - (GROUP_POSITION.get(b.key) ?? -1);
  if (byPosition !== 0) return byPosition;
  return (a.label ?? '').localeCompare(b.label ?? '');
};

const collapseDay = ({ candidates, date }: PendingDay): ImmunizationDose => {
  const sorted = [...candidates].sort(byAuthority);
  // Non-empty: a day is created with its first candidate.
  const shown = sorted[0]!;
  return {
    record: shown.record,
    merged: sorted.slice(1).map((candidate) => candidate.record),
    date,
    shortName: getCvxEntry(cvxCode(shown.record))?.shortName ?? sourceName(shown.record),
  };
};

/**
 * Group a patient's immunizations into one line per vaccine, with same-day rows for the same group
 * collapsed into one dose.
 *
 * Exporters describe one shot as several rows: a specific product code beside its "unspecified
 * formulation" code, or a trivalent beside a quadrivalent code. Rows in the same CDC vaccine group
 * on the same day are therefore one dose, and the most specific row is the one shown. Rows are never
 * collapsed across days, so a childhood series survives intact. A combination product is one dose in
 * each of its component groups, so "is this patient up to date on polio" reads correctly from a
 * DTaP-IPV row.
 *
 * Pure and synchronous. Callers pass every record they want considered: a group built from part of
 * a history has the wrong dose count and most-recent date.
 */
export const groupImmunizationRecords = (records: ImmunizationRecord[]): GroupedImmunizations => {
  const pending = new Map<string, PendingGroup>();
  const hidden: ImmunizationRecord[] = [];

  const addTo = (target: Omit<PendingGroup, 'days'>, candidate: Candidate): void => {
    let group = pending.get(target.key);
    if (!group) {
      group = { ...target, days: new Map() };
      pending.set(target.key, group);
    }
    const date = immunizationDay(candidate.record);
    const dayKey = date ?? `undated:${candidate.record.id}`;
    const day = group.days.get(dayKey);
    if (day) {
      day.candidates.push(candidate);
    } else {
      group.days.set(dayKey, { candidates: [candidate], date });
    }
  };

  for (const record of records) {
    const resolution = resolve(record);
    if (resolution.kind === 'placeholder') {
      hidden.push(record);
    } else if (resolution.kind === 'grouped') {
      for (const group of resolution.groups) {
        addTo(
          { key: group, kind: kindForGroup(group), group, label: undefined },
          { record, unspecified: resolution.unspecified, coded: cvxCode(record) !== undefined },
        );
      }
    } else {
      addTo(
        {
          key: `${resolution.kind}:${normalize(resolution.label)}`,
          kind: resolution.kind,
          group: undefined,
          label: resolution.label,
        },
        { record, unspecified: false, coded: false },
      );
    }
  }

  const groups = [...pending.values()].map((group): ImmunizationGroup => {
    const doses = [...group.days.values()].map(collapseDay).sort(byNewestDoseThenId);
    return {
      key: group.key,
      kind: group.kind,
      group: group.group,
      label: group.label,
      doses,
      mostRecent: doses.find((dose) => dose.date !== undefined)?.date,
    };
  });

  return { groups: groups.sort(byScheduleThenName), hidden };
};

/** Whether a group is a line on the vaccine schedule, as opposed to a test, immune globulin or
 * disease history. Unresolved rows count as vaccines: that is what the source called them. */
export const isVaccineScheduleGroup = (group: ImmunizationGroup): boolean =>
  group.kind === 'vaccine' || group.kind === 'unresolved';

/** English display name for a group: the CDC group's name, or the source label for rows with no
 * group. Hosts with an i18n tree translate `group` themselves instead. */
export const immunizationGroupName = (group: ImmunizationGroup): string =>
  group.group ? VACCINE_GROUP_NAMES[group.group] : (group.label ?? '');

/** Headline counts for a summary row: how many vaccines the patient has any dose of, and how many
 * distinct administrations those are. A combination dose is one administration however many groups
 * it counts toward. */
export const summarizeImmunizationGroups = (groups: ImmunizationGroup[]): { vaccines: number; doses: number } => {
  const vaccineGroups = groups.filter((group) => group.kind === 'vaccine');
  const doseIds = new Set(vaccineGroups.flatMap((group) => group.doses.map((dose) => dose.record.id)));
  return { vaccines: vaccineGroups.length, doses: doseIds.size };
};

/** Every record behind a group, displayed rows and merged rows alike, for callers that still work
 * on individual resources (promotion, detail lookup by id). */
export const immunizationGroupRecords = (group: ImmunizationGroup): ImmunizationRecord[] =>
  group.doses.flatMap((dose) => [dose.record, ...dose.merged]);
